import type { PermissionMode } from "./sdk/types";

export interface DesktopAccessPreferences {
  /** Remote Cloud/Mobile directory operations must stay below this path. */
  remoteControlRoot: string;
  /** Applied to newly-created sessions unless the user overrides it. */
  defaultPermissionMode: PermissionMode;
  /** User-facing name advertised by this computer. */
  computerName: string;
}

export function defaultDesktopAccessPreferences(
  sessionRoot = "~/Orni",
): DesktopAccessPreferences {
  return {
    remoteControlRoot: normalizeDirectory(sessionRoot) || "~/Orni",
    defaultPermissionMode: "workspace-write",
    computerName: "",
  };
}

/** DeviceSettings is the only durable authority for these preferences. */
export function mergeDeviceAccessPreferences(
  current: DesktopAccessPreferences,
  settings: unknown,
): DesktopAccessPreferences {
  if (!settings || typeof settings !== "object") return current;
  return normalizeDesktopAccessPreferences(
    settings as Record<string, unknown>,
    current,
  );
}

function normalizeDesktopAccessPreferences(
  source: Record<string, unknown>,
  fallback: DesktopAccessPreferences,
): DesktopAccessPreferences {
  const root = firstString(source, "remoteControlRoot", "remote_control_root");
  const name = firstString(source, "computerName", "computer_name", "deviceName", "device_name");
  const permission = firstString(
    source,
    "defaultPermissionMode",
    "default_permission_mode",
  );
  return {
    remoteControlRoot: normalizeDirectory(root) || fallback.remoteControlRoot,
    defaultPermissionMode: isPermissionMode(permission)
      ? permission
      : fallback.defaultPermissionMode,
    computerName: name.trim() || fallback.computerName,
  };
}

function firstString(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string") return value;
  }
  return "";
}

function isPermissionMode(value: string): value is PermissionMode {
  return (
    value === "read-only" ||
    value === "workspace-write" ||
    value === "full-access"
  );
}

function normalizeDirectory(value: string): string {
  const trimmed = value.trim();
  return trimmed.length > 1 ? trimmed.replace(/\/$/, "") : trimmed;
}
